import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons/faTrashCan';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Col,
  InputNumber,
  Row,
  Slider,
  type TableColumnsType,
  theme,
  Typography,
} from 'antd';
import { PosTable } from 'pos-client/components/shared/Display/Table/PosTable.tsx';
import { useJobTechnicians } from 'pos-client/contexts/JobTechniciansContext.tsx';
import { useAllUsers } from 'pos-client/hooks/useAllUsers.ts';
import { type LinkedTechniciansGroup } from 'pos-client/hooks/useLocationLinkedTechnicians.ts';
import { trpc } from 'pos-client/utils/trpc.ts';
import type { OrderJobTechnicianFeModel } from 'pos-shared/models/orderSchema.ts';

import { TechnicianSelect } from './TechnicianSelect.tsx';

type TechniciansTableProps = {
  technicianList: LinkedTechniciansGroup;
  isLoadingTechnicianList: boolean;
  technicianNotScheduled: boolean;
};

export const createSliderMarks = (maxValue: number) =>
  Object.fromEntries(
    Array.from({ length: Math.ceil(maxValue / 5) + 1 }, (_, i) => [
      i * 5,
      i * 5 === 0 || i * 5 === maxValue ? (i * 5).toString() : '',
    ])
  );

export const calculateMaxEffort = (
  effortPercentage: number,
  technicians: OrderJobTechnicianFeModel[]
) =>
  100 -
  technicians.reduce((total, tech) => total + (tech.effortPercentage ?? 0), 0) +
  effortPercentage;

export const TechniciansTable = ({
  technicianList,
  isLoadingTechnicianList,
  technicianNotScheduled,
}: TechniciansTableProps) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { users, isLoadingUsers } = useAllUsers();
  const { technicians, updateTechnician, removeTechnician } =
    useJobTechnicians();

  const { data: qcRatingsData, isLoading: isLoadingQcRatings } =
    trpc.findQualityRating.useQuery({
      pageNumber: 1,
      resultsPerPage: 25,
    });

  const tableData = useMemo(
    () =>
      technicians.map((tech) => ({
        ...tech,
        key: tech.userId,
      })),
    [technicians]
  );

  const onUpdateEffortPercentage = (
    technicianId: string,
    effortPercentage: number
  ) =>
    updateTechnician(technicianId, {
      effortPercentage: Math.floor(effortPercentage),
    });

  const onSelectFulfilmentTechnician = useCallback(
    (
      technicianName: string,
      technicianId: string,
      existingTechnicianId: string
    ) => {
      const technician = users?.results.find(({ id }) => id === technicianId);

      if (technician) {
        const qcRating = qcRatingsData?.results.find(
          ({ id }) => id === technician.qualityRatingId
        );
        updateTechnician(
          existingTechnicianId,
          {
            userId: technicianId,
            name: technicianName,
            qcRatingLevel: qcRating?.rating ?? 0,
          },
          existingTechnicianId
        );
      }
    },
    [users?.results, qcRatingsData?.results]
  );

  // Creates range markers for sliders based on the maxmium value a technician can go up to vs the total effort

  const columns: TableColumnsType<OrderJobTechnicianFeModel> = useMemo(
    () => [
      {
        title: t('technicians.technician'),
        key: 'technician',
        width: 400,
        render: (_, record) => (
          <TechnicianSelect
            technicians={technicianList}
            loading={isLoadingTechnicianList}
            onSelect={(_, option) =>
              onSelectFulfilmentTechnician(
                option.value as string,
                option.id,
                record.userId
              )
            }
            value={record.userId.includes('new') ? null : record.userId}
          />
        ),
      },
      {
        title: t('users.qualityRatings.qcRating'),
        key: 'qcRating',
        width: 80,
        render: (_, record) => record.qcRatingLevel.toString(),
      },
      {
        title: t('technicians.effort'),
        key: 'effortSlider',
        align: 'left',
        width: 250,
        render: (_, record) => (
          <Row
            align="middle"
            justify="center"
            style={{ paddingTop: token.padding, paddingBottom: token.padding }}
          >
            <Col span={18} style={{ paddingRight: token.paddingSM }}>
              <Slider
                min={0}
                max={100}
                step={5}
                onChange={(val) => onUpdateEffortPercentage(record.userId, val)}
                value={record.effortPercentage}
                marks={createSliderMarks(
                  calculateMaxEffort(record.effortPercentage, technicians)
                )}
                style={{ marginBottom: '9px' }}
              />
            </Col>
            <Col span={6}>
              <InputNumber
                type="number"
                size="small"
                min={0}
                max={calculateMaxEffort(record.effortPercentage, technicians)}
                style={{ maxWidth: 50, marginLeft: 10 }}
                step={5}
                controls={false}
                value={record.effortPercentage}
                onChange={(val) =>
                  onUpdateEffortPercentage(record.userId, val ?? 0)
                }
              />
            </Col>
          </Row>
        ),
      },
      {
        key: 'deleteTechnician',
        align: 'center',
        width: 50,
        render: (_, { userId }) => (
          <Button
            type="text"
            onClick={() => removeTechnician(userId)}
            icon={<FontAwesomeIcon icon={faTrashCan} />}
            size="small"
            danger
          />
        ),
      },
    ],
    [technicianList, users?.results, technicians]
  );

  return (
    <>
      <PosTable
        isLoading={
          isLoadingQcRatings || isLoadingUsers || isLoadingTechnicianList
        }
        columns={columns}
        dataSource={tableData}
        size="small"
      />
      {technicianNotScheduled && (
        <Row
          style={{
            paddingTop: token.marginLG,
            textAlign: 'center',
          }}
          align="top"
          justify="center"
        >
          <FontAwesomeIcon
            icon={faTriangleExclamation}
            color={token['gold-8']}
            style={{
              marginRight: token.marginXXS,
              marginTop: '4px',
            }}
          />
          <Typography.Text
            style={{
              color: token['gold-8'],
            }}
          >
            {t('technicians.selectNonScheduledTechnicianExtraWarning')}
          </Typography.Text>
        </Row>
      )}
    </>
  );
};
